// Profile Completion Page
// Implements onboarding flow from PRD Section 5.2

import { useState, useEffect } from 'react';
import { Navigate, useNavigate, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useAuth, useRefreshProfile } from '../hooks/useAuth';
import { userService } from '../services/api';
import {
  profileCompletionSchema,
  type ProfileCompletionData,
} from '../lib/schemas';
import { getErrorMessage } from '../lib/firebase-errors';
import { DisplayNameField } from '../components/form';
import { UserAvatar, LoadingSpinner } from '../components/ui';
import { validateReturnToPath } from '../lib/utils/navigation';

// Session storage key for form data
const FORM_SESSION_KEY = 'heapster-onboarding-form';

export function CompleteProfilePage() {
  const { user, profileCompleted, firebaseUser, isLoading } = useAuth();
  const refreshProfileMutation = useRefreshProfile();
  const navigate = useNavigate();
  const location = useLocation();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [avatarMessage, setAvatarMessage] = useState<{
    type: 'success' | 'error';
    text: string;
  } | null>(null);

  // Get SSO provider data
  const getSSODisplayName = () => {
    if (!firebaseUser?.providerData?.length) return '';
    const provider = firebaseUser.providerData[0];
    return provider?.displayName ?? '';
  };

  // Load saved form data from session storage
  const getSavedFormData = (): Partial<ProfileCompletionData> => {
    try {
      const saved = sessionStorage.getItem(FORM_SESSION_KEY);
      return saved ? JSON.parse(saved) : {};
    } catch {
      return {};
    }
  };

  const {
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useForm<ProfileCompletionData>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: {
      display_name:
        user?.display_name ||
        getSSODisplayName() ||
        getSavedFormData().display_name ||
        '',
    },
  });

  const displayName = watch('display_name');

  // Save form data to session storage on change
  useEffect(() => {
    const formData = { display_name: displayName };
    sessionStorage.setItem(FORM_SESSION_KEY, JSON.stringify(formData));
  }, [displayName]);

  // Show loading state while authentication is being determined
  if (isLoading) {
    return <LoadingSpinner fullScreen message="Loading profile..." />;
  }

  // Redirect if user is not authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Redirect if profile is already completed
  if (profileCompleted) {
    return <Navigate to="/dashboard" replace />;
  }

  const onSubmit = async (data: ProfileCompletionData) => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Update user profile
      const result = await userService.updateUserProfile(user.uid, {
        display_name: data.display_name,
      });

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to update profile');
      }

      // Refresh user profile data
      await refreshProfileMutation.mutateAsync();

      // Clear session storage
      sessionStorage.removeItem(FORM_SESSION_KEY);

      // Extract and validate returnTo from location state. Keep the query
      // string: flows like /oidc/continue?request_id=... carry one-time
      // state in it.
      const state = location.state as { returnTo?: Location } | null;
      const returnToPath = state?.returnTo
        ? `${state.returnTo.pathname}${state.returnTo.search ?? ''}`
        : undefined;
      const validatedPath = validateReturnToPath(returnToPath);
      const destination = validatedPath || '/dashboard';

      // Navigate to validated destination
      navigate(destination);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkip = async () => {
    if (!user) return;

    try {
      setIsSubmitting(true);
      setError(null);

      // Generate a default display name if none exists
      let defaultDisplayName = displayName || user?.display_name || '';

      // If still no display name, try to generate one
      if (!defaultDisplayName) {
        // Use email username part as fallback
        const emailUsername = user.email?.split('@')[0] || '';
        if (emailUsername) {
          // Clean up email username (remove dots, numbers at end, etc)
          defaultDisplayName = emailUsername
            .replace(/[._]/g, ' ')
            .replace(/\d+$/, '')
            .trim();
        }

        // Final fallback
        if (!defaultDisplayName) {
          defaultDisplayName = 'User';
        }
      }

      // Save minimal profile with just display name
      const result = await userService.updateUserProfile(user.uid, {
        display_name: defaultDisplayName,
      });

      if (!result.success) {
        throw new Error(result.error ?? 'Failed to skip profile setup');
      }

      // Refresh user profile data
      await refreshProfileMutation.mutateAsync();

      // Clear session storage
      sessionStorage.removeItem(FORM_SESSION_KEY);

      // Extract and validate returnTo from location state. Keep the query
      // string: flows like /oidc/continue?request_id=... carry one-time
      // state in it.
      const state = location.state as { returnTo?: Location } | null;
      const returnToPath = state?.returnTo
        ? `${state.returnTo.pathname}${state.returnTo.search ?? ''}`
        : undefined;
      const validatedPath = validateReturnToPath(returnToPath);
      const destination = validatedPath || '/dashboard';

      // Navigate to validated destination
      navigate(destination);
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      setError(errorMessage);
      setIsSubmitting(false);
    }
  };

  const handleAvatarUpload = async () => {
    // Avatar upload is handled by the UserAvatar component
    // Just refresh the profile to get the updated avatar
    setAvatarMessage({
      type: 'success',
      text: 'Profile photo uploaded successfully!',
    });
    await refreshProfileMutation.mutateAsync();
  };

  const handleAvatarError = (error: Error) => {
    setAvatarMessage({
      type: 'error',
      text: error.message ?? 'Failed to upload profile photo',
    });
  };

  return (
    <div className="complete-profile-page">
      <div className="auth-container">
        <div className="auth-header">
          <h1>Complete Your Profile</h1>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="auth-form">
          {error && <div className="error-banner">{error}</div>}

          {/* Avatar Upload Section */}
          <div className="avatar-section">
            <h3 className="section-title">Profile Photo (Optional)</h3>
            <div className="avatar-upload-container">
              <UserAvatar
                displayName={displayName ?? user?.display_name ?? 'User'}
                photoUrls={user?.photo_url}
                size="xl"
                editable={true}
                userId={user?.uid}
                onUploadComplete={() => {
                  void handleAvatarUpload();
                }}
                onUploadError={handleAvatarError}
              />
              <div className="avatar-help-text">
                <p>Add a profile photo</p>
                <p className="text-sm text-gray-500">
                  You can skip this step and add a photo later
                </p>
              </div>
            </div>
            {avatarMessage && (
              <div className={`avatar-message ${avatarMessage.type}`}>
                {avatarMessage.text}
              </div>
            )}
          </div>

          <DisplayNameField
            value={displayName || ''}
            onChange={value => setValue('display_name', value)}
            placeholder="How should others see you?"
            disabled={isSubmitting}
            required={true}
            id="display_name"
            label="Display Name"
            helpText="This is how you'll appear to other users."
          />
          {errors.display_name && (
            <div className="field-error">{errors.display_name.message}</div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="submit-button primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Complete Profile'}
            </button>

            <button
              type="button"
              onClick={handleSkip}
              className="submit-button secondary"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Skipping...' : 'Skip for Now'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
